import * as React from 'react';
import {
    Service,
    MenuService,
    ControllerService,
    Controller,
    MenuConfig,
    ToolbarService,
    StateManager,
} from '@tencent/workbench';
import type { Action, MenuItem, AddonInfo } from '@tencent/td-addon-sdk-docs';
import { ActionType } from '@editor/src/js/constants/common-action-type';
import { InsertAddonController } from '@framework/workbench/ui/insert/insert-addon/insert-addon-controller';
import { getIcon } from '@framework/workbench/ui/insert/insert-addon/config';
import i18n from '@i18next/i18n';
import IntelligentToolDescriptionCard from '@com-pc/intelligent-tool-description-card';
import { DIVIDER } from '@src/framework/workbench/schema/menu-schema/common';
import { wrapIdForBig } from '@framework/workbench/util/wrap-id-util';
import { TDWOperationType, TDWActionType, TDWModuleType, TDWVer5Type, EventCodeType, TDWFileType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { ADDON_ID_FORMULA, DISABLE_ADDON_ID_FOR_DRIVE } from '@src/slide/application/service/addon/addon-constants';
import { IMobileEditorAppServices } from '@src/slide/application/entry/toc/editor/mobile/mobile-editor-app-service.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import DocsConfig from '@tencent/docs-config';
import { ToolbarFileMenuConfig } from '@src/slide/infrastructure/module/dependent/config/config-manager-constants';
import { IAddonHandler } from '@src/slide/application/service/addon/addon-handler.interface';
import { FIXED_KEYS_DOUBLE } from '../../schema/constant';
import _debounce from 'lodash/debounce';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { IPcEditorEditableServiceLayer } from '@src/slide/application/entry/toc/editor/pc/service-layer/editable-service-layer.interface';

interface IMenuConfig {
    key: string;
    items: any;
}

export const INTELLIGENT_TOOL_LOADED_FLAG = `${ActionType.INTELLIGENT_TOOL}-load`;
export const INSERT_IFRAME_LOADED_FLAG = `${ActionType.INSERT_IFRAME}-load`;
// 初始的「扩展插件」面板项
export const INITIAL_UNOFFICIAL_MALLS = [DIVIDER, ActionType.ADDON_MALL_SETTING, ActionType.ADDON_MALL_SHOP];
// 初始的「第三方插件」项
export const INITIAL_EXTERNAL_ADDONS = [ActionType.EXTERNAL_ADDONS_PANEL_TITLE];
const UNOFFICIAL_ADDON_PREFIX = 'addon-unoffcial-';
const INSERT_ADDON_PREFIX = 'addon-insert-';    // 插入-第三方内容 插件

/**
 * 插件
 */
@Service()
export class BaseAddonService {
    public static addonInfoMap: { [x: string]: {addonId: string, name: string} } = {};
    public addonMeta;
    public addonMetaMap: Map<string, AddonInfo> = new Map();
    public toolbarButtonHandles = new Map<string, (() => Promise<unknown>)>();
    public AddonUnofficialController;
    public clickUnOfficialCallbacks = new Map<string, () => void>();  // 缓存动态加载的插件的点击回调
    private unofficialAddonList: string[] = [];
    private readonly docsAddonSdk: Promise<any>;
    private debounceUpdate = _debounce(() => {
        this.toolbarService.$forceUpdate();
    });

    public constructor(
        private controllerService: ControllerService,
        private menuService: MenuService,
        private readonly stateManager: StateManager,
        private readonly toolbarService: ToolbarService,
    ) {
        this.registerAsyncMenuState();
        this.toolbarButtonHandles.set(ActionType.INTELLIGENT_TOOL, this.updateAddonList.bind(this));
        this.toolbarButtonHandles.set(wrapIdForBig(ActionType.INTELLIGENT_TOOL), this.updateAddonList.bind(this));
        if (!browser.isMobile) {
            import(/* webpackChunkName: "addon" */ '@framework/workbench/ui/addon_unofficial/addon-unofficial-controller')
                .then((module) => {
                    this.AddonUnofficialController = module.AddonUnofficialController;
                })
                .catch((error) => {
                    window.log.error(`[loadChunk-error]${error}`);
                });
        }
        this.docsAddonSdk = import(/* webpackChunkName: "addon" */ '@tencent/td-addon-sdk-docs');
    }

    public async updateAddonList() {
        const { DocAddonBridge } = await this.docsAddonSdk;
        let addons: AddonInfo[] = await (await DocAddonBridge.fetchAddonList())
            .filter(item => !item.isOfficial);
        let needUpdate = false;
        const newMenuEntry: AddonInfo[] = [];

        // 删除已卸载插件
        Array.from(this.addonMetaMap.keys()).forEach((key) => {
            if (!addons.find(({ addonId }) => addonId === key)) {
                this.unofficialAddonList.splice(this.unofficialAddonList.indexOf(key), 1);
                this.addonMetaMap.delete(key);
                needUpdate = true;
            }
        });

        // cac配置为不支持iframe插件
        if (!(DocsConfig.getInstance().getConfig(ToolbarFileMenuConfig.addonUnofficialIframe) ?? true)) {
            addons = addons.filter(item => DISABLE_ADDON_ID_FOR_DRIVE.indexOf(item.addonId) === -1);
        }

        // 增加已安装按钮插件
        addons.forEach((addon) => {
            const { addonId } = addon;
            if (!this.addonMetaMap.has(addonId)) {
                this.unofficialAddonList.unshift(addonId);
                this.addonMetaMap.set(addonId, addon);
                needUpdate = true;
                newMenuEntry.push(addon);
            }
        });

        if (needUpdate) {
            for (const item of newMenuEntry) {
                await this.updateAddonMenuSchema.call(this, item);
            }
            this.updateUnofficialAddonMenuSchema();
            (this.getAddonManager()?.addonHandler as IAddonHandler).updateAddonInfoList?.();
        }
    }

    /**
     * 该API为函数所用，只支持没有多级菜单的插件
     * @param addonId 执行操作的插件id
     */
    public async openAddonForMall(addonId): Promise<void> {
        try {
            const { DocAddonBridge } = await this.docsAddonSdk;
            const addon: AddonInfo = await DocAddonBridge.getAddon(addonId);
            await DocAddonBridge.fetchAddonMenu(addonId);
            await DocAddonBridge.executeMenuAction(addonId, addon.menuConfiguration as Action);
        } catch (error) {
            window.log?.error(error);
        }
    }

    /**
     * 接入workbench动态更新菜单，需要在拿到动态数据后，重新构建并注册workbench的schema与controller
     * 区分一/二级菜单，用于分别处理菜单列表和单项（目前包managers括插入第三方内容和智能插件）
     * @param addonMeta 插件元数据，调用来源是addon-handler.ts
     */
    public async updateAddonMenuSchema(addonMeta: AddonInfo) {
        this.addonMeta = addonMeta;
        const { menuConfiguration, addonId, isOfficial } = addonMeta;
        const { DocAddonBridge, MenuConfigurationType } = await this.docsAddonSdk;
        if (menuConfiguration) {
            const menuConfigurationType = DocAddonBridge.getMenuConfigurationType(menuConfiguration);
            switch (menuConfigurationType) {
                // 有一级菜单，比如插入第三方内容
                case MenuConfigurationType.MENUS: {
                    const menus = menuConfiguration as MenuItem[];
                    const { menuNames, menuItems } = await this.updateAddonMenu(addonMeta, menus);

                    // 加入插件二级菜单
                    const newSchema = this.menuService.updateKeySchema(
                        ActionType.INSERT_IFRAME,
                        menuNames,
                    );

                    // 更新schema
                    this.menuService.setSchema(newSchema);

                    // 更新controller
                    this.updateController(menuItems, InsertAddonController);
                    this.stateManager.update(INSERT_IFRAME_LOADED_FLAG, true);
                    break;
                }
                // 没有一级菜单，比如iSlide
                case MenuConfigurationType.ACTION: {
                    /**
                     * 判断是latex还是智能工具，搬运addon-handler
                     */
                    if (addonId === ADDON_ID_FORMULA) {
                        this.updateLatexAddonMenu(addonMeta);
                    }

                    // 渲染插件
                    if (!isOfficial) {
                        // 重新构建插件controller
                        this.updateUnOfficial(addonMeta, menuConfiguration as Action);
                        this.stateManager.update(INTELLIGENT_TOOL_LOADED_FLAG, true);
                    }
                    break;
                }
                default:
                    break;
            }
            this.debounceUpdate();
        }
    }

    // 为第三方插件更新 schema
    private updateUnofficialAddonMenuSchema() {
        // 过滤腾讯会议插件；后端每次返回的插件顺序不确定，sort 保持前端显示顺序
        const unofficialAddons = this.unofficialAddonList.sort();
        // 根据是否接收到第三方插件去动态更新 schema
        const newAddons = this.getSchemeKeys(unofficialAddons);
        const newSchema = this.menuService.updateKeySchema(ActionType.INTELLIGENT_TOOL, newAddons);
        this.menuService.setSchema(newSchema);
        this.menuService.$forceUpdate();
    }

    private getSchemeKeys(unofficialAddons: string[]): string[] {
        let initialKeys: string[] = [];
        if (unofficialAddons.length !== 0) {
            // 更新非官方插件前缀
            const addonKeyList = unofficialAddons.map(item => `${UNOFFICIAL_ADDON_PREFIX}${item}`);
            initialKeys = initialKeys.concat(INITIAL_EXTERNAL_ADDONS, addonKeyList);
        }
        return initialKeys.concat(INITIAL_UNOFFICIAL_MALLS);
    }

    /**
     * 动态的拿到新menu config后，重新构建并注册controller，
     * @param menus 需要更新的config
     * @param controller 待更新的controller
     * @private
     */
    private updateController(menus: MenuConfig[], controller) {
        Controller({ menuItems: menus })(controller);
        this.controllerService.buildController(controller);
    }

    /**
     * 构建workbench controller需要的menu config
     * @param addonInfo
     * @param itemsMenu
     * @private
     */
    private async updateAddonMenu(addonInfo: AddonInfo, itemsMenu: MenuItem[]) {
        const menuNames: string[] = [];
        const menuItems: MenuConfig[] = [];
        const host = `//${addonInfo.staticHost}/${addonInfo.staticPath}`;
        let i = 0;
        const { MenuType } = await this.docsAddonSdk;
        itemsMenu?.forEach((citem) => {
            if (citem.type === MenuType.DIVIDE) {
                menuNames.push('divider');
            } else {
                const menuName = `${INSERT_ADDON_PREFIX}${addonInfo.addonId}_${i}`;
                menuNames.push(menuName);
                i += 1;
                const icon = `${host}/${citem.icon}`;
                const menu = {
                    name: menuName,
                    content: i18n.t<string>(citem.title || ''),
                    icon: getIcon(icon),
                };
                menuItems.push(menu);
            }
        });
        return { menuNames, menuItems };
    }

    /**
     * 更新latex公式
     * @param addonInfo
     * @private
     */
    private updateLatexAddonMenu(addonInfo: AddonInfo) {
        const { addonId, name } = addonInfo;
        BaseAddonService.addonInfoMap[ActionType.INSERT_WITH_LATEX] = { addonId, name };

        this.updateLatexAddonMenuSchema();
        this.updateLatexAddonToolSchema();
    }

    private updateLatexAddonMenuSchema() {
        const menuSchema = this.menuService.getSchema() as IMenuConfig[];
        const insertMenu = menuSchema.find(item => item.key === ActionType.INSERT_ALL);
        const { items = [] } = insertMenu || {};

        // 查找页脚的schema，插入到页脚的下一个位置
        let i = 0;
        items.forEach((item, index) => {
            if (item === ActionType.INSERT_SLIDE_FOOTER) {
                i = index + 1;
            }
        });
        if (items[i] === ActionType.INSERT_WITH_LATEX) {
            // 如果已经插入了就不要在插一遍了
            return;
        }
        items.splice(i, 0, ActionType.INSERT_WITH_LATEX);
        const newSchema = this.menuService.updateKeySchema(
            ActionType.INSERT_ALL,
            items,
        );

        // 调用workbench本身提供的api，用于主动更新schema
        this.menuService.setSchema(newSchema);
    }

    private updateLatexAddonToolSchema() {
        const toolSchema = this.toolbarService.getSchema();
        if (!toolSchema?.[FIXED_KEYS_DOUBLE.TOOLBAR_INSERT]?.data) return;
        // eslint-disable-next-line no-restricted-syntax
        for (const group of toolSchema[FIXED_KEYS_DOUBLE.TOOLBAR_INSERT].data) {
            const { items } = group as any;
            for (let index = 0; index < items.length; index++) {
                const item = items[index];
                const buttonIndex = item.buttons
                    .findIndex(button => button[0] === wrapIdForBig(ActionType.INSERT_WITH_LINK));
                if (buttonIndex !== -1) {
                    items.splice(index + 1, 0, {
                        key: ActionType.INSERT_WITH_LATEX,
                        buttons: [[wrapIdForBig(ActionType.INSERT_WITH_LATEX)]],
                    });
                    return;
                }
            }
        }

        this.toolbarService.setSchema(toolSchema);
    }

    /**
     * 更新智能工具
     */
    private updateUnOfficial(addonInfo: AddonInfo, action?: Action) {
        const {
            addonId,
        } = addonInfo;
        const menuSchema = this.menuService.getSchema() as IMenuConfig[];
        if (!this.addonMetaMap.has(addonId)) {
            this.unofficialAddonList.unshift(addonId);
            this.addonMetaMap.set(addonId, addonInfo);
        }

        // wb不能在创建Controller时直接添加shouldDisabled回调，需要对其id进行包装后在Controller中通过装饰器添加
        const menuId = `${UNOFFICIAL_ADDON_PREFIX}${addonId}`;
        const { items = [] } = menuSchema.find(item => item.key === ActionType.INTELLIGENT_TOOL) || {};
        if (items.indexOf(menuId) !== -1) return;
        if (items[0] !== ActionType.EXTERNAL_ADDONS_PANEL_TITLE) {
            items.unshift(ActionType.EXTERNAL_ADDONS_PANEL_TITLE, DIVIDER);
        }
        items.splice(items.length - 3, 0, menuId);
        const newSchema = this.menuService.updateKeySchema(
            ActionType.INTELLIGENT_TOOL,
            items,
        );
        this.menuService.setSchema(newSchema);
        // @ts-ignore
        const { icon, title, vendorSite, imageUrl, description, vendorName } = {
            icon: addonInfo.icons.small,
            title: addonInfo.name,
            vendorName: addonInfo.vendorName,
            imageUrl: addonInfo.icons.intro,
            description: addonInfo.shortDesc,
            vendorSite: addonInfo.vendorSite,
        };
        const needDisable = (browser.isPc && browser.isMiniProgram);

        this.clickUnOfficialCallbacks.set(addonId, () => {
            if (action) {
                this.executeMenuAction(addonId, action);
                // 部分插件使用到了wb的drawer
                this.getEditableServiceLayer()?.drawerStateManager.promise().then((ins) => {
                    ins.handleAddonDrawerAfterOpened();
                });
            }
            this.twdReportAddon(addonId, addonInfo.name);
        });
        this.updateController([
            {
                name: menuId,
                icon: getIcon(icon),
                content: title,
                tooltip: needDisable ? {
                    content: i18n.t('请到电脑客户端或浏览器中使用'),
                    placement: 'left',
                    style: {
                        display: 'block',
                    },
                } : {
                    content: <IntelligentToolDescriptionCard
                        vendorName={vendorName}
                        title={title}
                        imageUrl={imageUrl}
                        description={description}
                        vendorSite={vendorSite}
                    />,
                    alignment: 'start',
                    style: {
                        display: 'block',
                    },
                },
            }], this.AddonUnofficialController);
    }

    private async executeMenuAction(addonId: string, action: Action) {
        try {
            const { DocAddonBridge } = await this.docsAddonSdk;
            DocAddonBridge.executeMenuAction(addonId, action);
        } catch (error) {
            window.log?.error(error);
        }
    }


    /**
     * 注册异步菜单的初始状态
     */
    private registerAsyncMenuState() {
        this.stateManager.register(INTELLIGENT_TOOL_LOADED_FLAG, false);
        this.stateManager.register(INSERT_IFRAME_LOADED_FLAG, false);
    }

    private twdReportAddon(addonId: string, name: string) {
        tdw({
            opername: TDWOperationType.DOC_ADDON,
            module: TDWModuleType.ADDON_LIST,
            action: TDWActionType.CLK_ADDON,
            ver5: TDWVer5Type.DOC_DETAIL_TOOLBAR,
            ver6: addonId,
            ver7: name,
            eventcode: EventCodeType.DOC_ADDON_ADDON_LIST_CLK_ADDON,
            file_type: TDWFileType.SLIDE,
        });
    }

    private getEditableServiceLayer(): IPcEditorEditableServiceLayer| undefined {
        return (AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices).editableServiceLayer;
    }

    private getAppServices(): IMobileEditorAppServices {
        return AppServiceProvider.getInstance().getAppServices() as
            IMobileEditorAppServices;
    }

    private getAddonManager = () => this.getAppServices().viewableServiceLayer.addonManager;
}
